import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { map } from "lodash";
import moment from "moment";
import { CpCalendar, CpDatePicker, CpLabel } from "@components";
import { dateRangeTypes } from "./date-filter.helper";
import { toggle } from "src/utils";

const momentToString = (momentValue) => {
  if (!momentValue) return "";
  return moment.isMoment(momentValue)
    ? momentValue.format("YYYY-MM-DD")
    : moment(momentValue).format("YYYY-MM-DD");
};

export const RelativeDateRangeFilter = ({
  applyFilter,
  filter,
  dateRanges,
  hideSortOptions,
}) => {
  const [dateRange, setDateRange] = useState(["", ""]);
  const [startDate, endDate] = dateRange;
  const isFixedDate = filter.filter_params === dateRangeTypes.custom;

  useEffect(() => {
    let startDateStr = "";
    let endDateStr = "";

    if (filter.after || filter.before) {
      startDateStr = momentToString(filter.after);
      endDateStr = momentToString(filter.before);
    } else if (filter.filter_params && !isFixedDate) {
      const range = dateRanges[filter.filter_params];
      if (range) {
        startDateStr = momentToString(range.filter.after);
        endDateStr = momentToString(range.filter.before);
      }
    }

    setDateRange([startDateStr, endDateStr]);
  }, [filter, dateRanges, isFixedDate]);

  const updateDates = (start, end) => {
    const orderedDates =
      start && end && moment(start).isAfter(moment(end))
        ? [end, start]
        : [start || "", end || ""];

    setDateRange(orderedDates);
    applyFilter(dateRangeTypes.custom, {
      after: orderedDates[0] ? moment(orderedDates[0]) : null,
      before: orderedDates[1] ? moment(orderedDates[1]) : null,
    });
  };

  const handleRangeChange = (value) => {
    if (Array.isArray(value)) {
      const [start, end] = value;
      if (start === startDate && end === endDate) return;
      setDateRange(value);
      applyFilter(dateRangeTypes.custom, {
        after: start ? moment(start) : null,
        before: end ? moment(end) : null,
      });
    }
  };

  const handleStartDateChange = (value) => updateDates(value, endDate);
  const handleEndDateChange = (value) => updateDates(startDate, value);

  return (
    <div className="flex">
      <div>
        {!hideSortOptions && (
          <li className="cps-divider cps-margin-left-16 cps-margin-right-16 cps-margin-top-4 cps-margin-bottom-4" />
        )}
        {map(dateRanges, (dateRange, dateRangeKey) => {
          return (
            <li key={dateRangeKey} className="cursor-pointer w-[20rem]">
              <div
                className="flex items-center justify-between ml-4 mr-4 pt-2 pb-2"
                onClick={() => applyFilter(dateRangeKey, dateRange.filter)}
              >
                <div
                  className={
                    filter && filter.filter_params === dateRangeKey
                      ? "cps-color-primary cp-wt-semibold"
                      : ""
                  }
                >
                  {dateRange.label}
                </div>
                <div className="min-w-[25px] m-[-0.3rem]">
                  {filter && filter.filter_params === dateRangeKey && (
                    <i className="cps-color-primary cps-icon cps-icon-sm-check" />
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </div>
      <div
        /** The way menu-content.component is structured requires negative margin here to account for the sort options adding height. Ideally we should refactor or move away from cp-table-header-menu  */
        className={`border-l border-[var(--cp-color-app-border)] p-4 pt-0 ml-6 ${toggle(hideSortOptions, "mt-0", "mt-[-76px]")}`}
      >
        <div className="mb-4 w-[26rem] h-[29.2rem] overflow-hidden">
          <CpCalendar
            selectRange
            value={dateRange}
            onChange={handleRangeChange}
            isRelativeRange={!isFixedDate}
          />
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
          <CpLabel className="text-right">Start date</CpLabel>
          <CpDatePicker
            value={startDate}
            onChange={handleStartDateChange}
            disableCalendar
            block
            clearable
          />
          <CpLabel className="text-right">End date</CpLabel>
          <CpDatePicker
            value={endDate}
            onChange={handleEndDateChange}
            disableCalendar
            block
            clearable
          />
        </div>
      </div>
    </div>
  );
};

RelativeDateRangeFilter.propTypes = {
  applyFilter: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  dateRanges: PropTypes.object.isRequired,
  hideSortOptions: PropTypes.bool,
};
